import { useEffect, type ReactNode } from "react";
import { useLocation } from "react-router-dom";
import { PublicHeader } from "./PublicHeader";
import { PublicFooter } from "./PublicFooter";

/** Scrolls the public scroll container to the URL hash on navigation, so
 *  cross-page anchor links (e.g. /#pricing from a deep page) land on the right
 *  section. Honors prefers-reduced-motion. The home sections set scroll-margin-top
 *  so the target clears the sticky header. */
function useScrollToHash() {
  const { hash, pathname } = useLocation();
  useEffect(() => {
    if (!hash) {
      // Fresh page (no hash) starts at the top of its own scroll container.
      document.querySelector(".landing-root")?.scrollTo({ top: 0 });
      return;
    }
    const id = decodeURIComponent(hash.slice(1));
    // Let the route's content render before we look for the target.
    const raf = requestAnimationFrame(() => {
      const el = document.getElementById(id);
      if (!el) return;
      const reduce = window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;
      el.scrollIntoView({ behavior: reduce ? "auto" : "smooth", block: "start" });
    });
    return () => cancelAnimationFrame(raf);
  }, [hash, pathname]);
}

/** Shared chrome for every public marketing page: the light-pinned scroll
 *  container, skip link, sticky header, and footer. Page content is the children
 *  inside <main>. Kept free of app-internal imports so the surface stays cleanly
 *  liftable to a standalone site. */
export function PublicLayout({ children }: { children: ReactNode }) {
  useScrollToHash();
  return (
    <div className="landing-root">
      <a href="#main" className="landing-skip">Skip to content</a>
      <PublicHeader />
      <main id="main" tabIndex={-1}>{children}</main>
      <PublicFooter />
    </div>
  );
}
